import { ModeForm } from "constants/enum";
import {
  DEFAULT_PAGE_INDEX_INPUT,
  DEFAULT_PAGE_SIZE,
  DELETE_SUCCESS_MESSAGE,
  REACT_APP_API_BASE_URL,
} from "constants/index";

import { useCallback, useEffect, useMemo, useState } from "react";

import { Box, Grid, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import {
  Button,
  DotsVerticalIcon,
  Download01Icon,
  DownloadCloud02Icon,
  Edit02Icon,
  EyeIcon,
  PlusIcon,
  Trash02Icon,
  Typography,
} from "components";
import { Table } from "components/Table/Table";
import { blue, gray } from "configs/colors";
import { confirm } from "contexts";
import { saveAs } from "file-saver";
import {
  ChecklistEntity,
  RequestCandidateEntity,
  RequestCandidateEntityStatus,
  RequestEntity,
  RequestEntityStatus,
  useDeleteOneBaseRequestCandidateControllerRequestCandidateEntity,
  useGetManyBaseRequestCandidateControllerRequestCandidateEntity,
} from "generated";
import { isJson } from "helpers/string";
import useEventSource from "hooks/useEventSource";
import { useNotification } from "hooks/useNotification";
import { useParams } from "react-router-dom";
import { NumberParam, useQueryParams, withDefault } from "use-query-params";
import storage from "utils/storage";

import { requestCandidateColumn } from "./columns";
import { RequestCandidateCreateComponent } from "./RequestCandidateCreateComponent";
import { RequestCandidateUpdateComponent } from "./RequestCandidateUpdateComponent";

interface Row {
  status: RequestCandidateEntityStatus;
  id: string;
}
interface Props {
  isRefetch: boolean;
  setDisableRequestField: any;
  dataRequest: RequestEntity;
}

export const RequestCandidateList = (props: Props) => {
  const { isRefetch, setDisableRequestField, dataRequest } = props;
  const [rowOpenOptionId, setRowOpenOptionId] = useState<Row | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [requestCandidateId, setRequestCandidateId] = useState<string>("");
  const [isModalCreateOpen, setIsModalCreateOpen] = useState(false);
  const [isModalEditOpen, setIsModalEditOpen] = useState(false);
  const [isEditForm, setIsEditForm] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<string[]>([]);
  const [selectedRequestCandidate, setSelectedRequestCandidate] = useState<
    RequestCandidateEntity[]
  >([]);
  const [allowDownload, setAllowDownload] = useState(false);
  const { id: requestId } = useParams();
  const [query, setQuery] = useQueryParams({
    pageIndex: withDefault(NumberParam, DEFAULT_PAGE_INDEX_INPUT),
    pageSize: withDefault(NumberParam, DEFAULT_PAGE_SIZE),
  });
  const [idTemplateDownload, setIdTemplateDownload] = useState("");
  const { pageIndex, pageSize } = query;

  const { data, isLoading, error, refetch } =
    useGetManyBaseRequestCandidateControllerRequestCandidateEntity({
      s: JSON.stringify({
        requestId: {
          $eq: requestId,
        },
      }),
      limit: pageSize,
      offset: (pageIndex - 1) * pageSize,
      join: [
        `checklists`,
        // `checklists.template`,
        `request`,
        `request.client`,
        `template`,
        `template.templateType`,
      ],
    });
  // eslint-disable-next-line

  const handleDownloadCV = useCallback(
    (id: string, fileName: string | undefined) => {
      confirm({
        title: "Download Candidate CV",
        content: "Do you want to download?",
      })
        .then(() => {
          const fileUrl = `${REACT_APP_API_BASE_URL}/request-candidate/download/${id}`;

          const token = storage.getToken();

          const headers = new Headers();
          headers.append("Authorization", `Bearer ${token}`);

          fetch(fileUrl, { headers })
            .then((response) => response.blob())
            .then((blob) => {
              saveAs(blob, fileName);
            })
            .catch(() => {});
        })
        .catch(() => {});
    },
    []
  );

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    params: GridRenderCellParams<RequestCandidateEntity>
  ) => {
    setAnchorEl(event.currentTarget);
    setRowOpenOptionId({
      id: params?.row?.id as string,
      status: params?.row?.status,
    });
  };

  const handleClose = () => {
    setRowOpenOptionId(null);
    setAnchorEl(null);
  };

  useEffect(() => {
    refetch();
    if (
      data?.data?.data?.filter((e) => {
        return e.status === RequestCandidateEntityStatus.New;
      }).length !== data?.data?.data?.length
    ) {
      // eslint-disable-next-line
      setDisableRequestField(true);
    }
  }, [isRefetch, refetch, data?.data?.data, setDisableRequestField]);

  const {
    mutate: deleteRequestCandidate,
    error: errDelete,
    isSuccess: isSuccessDelete,
  } = useDeleteOneBaseRequestCandidateControllerRequestCandidateEntity();

  const handleDelete = useCallback(
    (id: string) => {
      confirm({ title: "Delete", content: "Do you want to delete?" })
        .then(() => {
          deleteRequestCandidate(
            {
              id,
            },
            {
              onSuccess: () => {
                refetch();
              },
            }
          );
        })
        .catch(() => {});
    },
    [deleteRequestCandidate, refetch]
  );
  const handleDownloadReport = useCallback(
    (id: string, nameFile?: string | null) => {
      confirm({ title: "Download report", content: "Do you want to download?" })
        .then(() => {
          const fileUrl = `${REACT_APP_API_BASE_URL}/checklist/download-report/${id}`;

          const token = storage.getToken();

          const headers = new Headers();
          headers.append("Authorization", `Bearer ${token}`);

          fetch(fileUrl, { headers })
            .then((response) => response.blob())
            .then((blob) => {
              saveAs(blob, nameFile ?? "download");
            })
            .catch((e) => {});
        })
        .catch(() => {});
    },
    []
  );

  const getParams = () => {
    let checklistIds: string[] = [];
    selectedRequestCandidate.forEach((e) => {
      if (e.checklists && e.checklists.length > 0) {
        checklistIds = [
          ...checklistIds,
          ...e.checklists.map((checklist) => checklist.id as string),
        ];
      }
    });
    if (checklistIds && checklistIds.length > 0) {
      const params = checklistIds.map((e) => `ids=${e}`).join("&");
      return params;
    }
    return "";
  };

  const {
    data: dataDownload,
    isComplete,
    isLoading: isDownloading,
  } = useEventSource(
    `${REACT_APP_API_BASE_URL}/multi-checklist/download-reports?${getParams()}`,
    allowDownload && !!getParams()
  );
  useEffect(() => {
    if (dataDownload && isComplete) {
      setRowSelectionModel([]);
      setSelectedRequestCandidate([]);
      setAllowDownload(false);
      saveAs(dataDownload);
    }
  }, [dataDownload, isComplete]);

  const handleDownloadReports = () => {
    setAllowDownload(true);
  };

  const open = Boolean(anchorEl);
  const columns = useMemo<GridColDef<RequestCandidateEntity>[]>(() => {
    return [
      {
        field: "candidateName",
        headerName: "Name",
        width: 250,
        renderCell: (params: GridRenderCellParams<RequestCandidateEntity>) => {
          return (
            <Typography
              type="TextSM"
              color={gray[600]}
              onClick={() => {
                if (params?.row?.status === RequestCandidateEntityStatus.New) {
                  setIsModalEditOpen(true);
                }
              }}
              sx={{
                color:
                  params?.row?.status !== RequestCandidateEntityStatus.New
                    ? undefined
                    : blue[700],
                cursor:
                  params?.row?.status !== RequestCandidateEntityStatus.New
                    ? undefined
                    : "pointer",
              }}
            >
              {params?.row?.candidateName}
            </Typography>
          );
        },
      },
      ...requestCandidateColumn,
      {
        field: "id",
        headerName: "Action",
        renderCell: (params: GridRenderCellParams<RequestCandidateEntity>) => {
          return (
            <Grid container>
              <Grid
                item
                xs={3}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <Tooltip title="View a candidate">
                  <IconButton
                    onClick={() => {
                      setIsEditForm(false);
                      setIsModalEditOpen(true);
                      setRequestCandidateId(params?.row?.id as string);
                    }}
                  >
                    <EyeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid
                item
                xs={3}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <Tooltip title="Edit a candidate cac">
                  <IconButton
                    onClick={() => {
                      setIsEditForm(true);
                      setIsModalEditOpen(true);
                      setRequestCandidateId(params?.row?.id as string);
                    }}
                    disabled={
                      (dataRequest?.status !== RequestEntityStatus.Rejected &&
                        dataRequest?.status !== RequestEntityStatus.Draft) ||
                      params?.row?.status !== RequestCandidateEntityStatus.New
                    }
                  >
                    <Edit02Icon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid
                item
                xs={3}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <Tooltip title="Download Candidate CV">
                  <IconButton
                    onClick={() => {
                      handleDownloadCV(
                        params?.row?.id as string,
                        isJson(params?.row?.fileUrl)
                          ? (JSON.parse(params?.row?.fileUrl)
                              ?.originalName as string)
                          : undefined
                      );
                    }}
                  >
                    <DownloadCloud02Icon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid
                item
                xs={3}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <Tooltip title="View more action">
                  <>
                    <IconButton
                      id="basic-button"
                      aria-controls={open ? "basic-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={open ? "true" : undefined}
                      onClick={(event) => {
                        handleClick(event, params);
                      }}
                    >
                      <DotsVerticalIcon fontSize="small" />
                    </IconButton>
                    <Menu
                      id="basic-menu"
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                      MenuListProps={{
                        "aria-labelledby": "basic-button",
                      }}
                    >
                      <Tooltip title="Download report" placement="left-start">
                        <MenuItem
                          onClick={() => {
                            handleDownloadReport(
                              (params?.row?.checklists as ChecklistEntity[])[0]
                                ?.id as string,
                              isJson(
                                (
                                  params?.row?.checklists as ChecklistEntity[]
                                )[0]?.fileUrl as string
                              )
                                ? (JSON.parse(
                                    (
                                      params?.row
                                        ?.checklists as ChecklistEntity[]
                                    )[0]?.fileUrl as string
                                  )?.fileName as string)
                                : null
                            );
                          }}
                          disabled={
                            rowOpenOptionId?.status !==
                            RequestCandidateEntityStatus.Published
                          }
                        >
                          <Download01Icon fontSize="small" />
                        </MenuItem>
                      </Tooltip>
                      <Tooltip
                        title="Delete Candidate info"
                        placement="left-start"
                      >
                        <div>
                          <MenuItem
                            onClick={() => {
                              handleClose();
                              handleDelete(rowOpenOptionId?.id as string);
                            }}
                            disabled={
                              (dataRequest?.status !==
                                RequestEntityStatus.Rejected &&
                                dataRequest?.status !==
                                  RequestEntityStatus.Draft) ||
                              rowOpenOptionId?.status !==
                                RequestCandidateEntityStatus.New
                            }
                          >
                            <Trash02Icon fontSize="small" />
                          </MenuItem>
                        </div>
                      </Tooltip>
                      <Tooltip title="View Report" placement="left-start">
                        <div>
                          <MenuItem
                            onClick={() => {
                              if (
                                params?.row.checklists &&
                                params?.row.checklists?.length > 0
                              ) {
                                window.open(
                                  JSON.parse(
                                    params?.row.checklists[0]?.fileUrl as string
                                  )?.fileUrl as string
                                );
                              }
                            }}
                            disabled={
                              rowOpenOptionId?.status !==
                              RequestCandidateEntityStatus.Published
                            }
                          >
                            <EyeIcon fontSize="small" />
                          </MenuItem>
                        </div>
                      </Tooltip>
                    </Menu>
                  </>
                </Tooltip>
              </Grid>
            </Grid>
          );
        },
        width: 200,
      },
    ];
  }, [
    handleDownloadReport,
    anchorEl,
    handleDelete,
    rowOpenOptionId,
    open,
    handleDownloadCV,
    dataRequest,
  ]);
  useNotification(error);
  useNotification(errDelete, isSuccessDelete, DELETE_SUCCESS_MESSAGE);
  return (
    <>
      <Box display="flex" justifyContent="space-between">
        <Typography type="TextLG" fontWeight={600}>
          Candidate list
        </Typography>
        {dataRequest?.status === RequestEntityStatus.Draft ||
        dataRequest?.status === RequestEntityStatus.Rejected ? (
          <Tooltip title="Add new Candidate" placement="top">
            <div>
              <Button
                type="submit"
                startIcon={<PlusIcon />}
                onClick={() => {
                  setIsModalCreateOpen(true);
                }}
              >
                Add Candidate
              </Button>
            </div>
          </Tooltip>
        ) : null}
        {data?.data.data.some(
          (e) => e.status === RequestCandidateEntityStatus.Published
        ) ? (
          <Tooltip title="Download reports (maximum of 10)" placement="top">
            <div>
              <Button
                startIcon={<DownloadCloud02Icon />}
                onClick={handleDownloadReports}
                loading={isDownloading}
                disabled={rowSelectionModel.length === 0}
              >
                Download reports
              </Button>
            </div>
          </Tooltip>
        ) : null}
      </Box>
      <Table
        isLoading={isLoading}
        columns={columns}
        rows={data?.data.data ?? []}
        pageSizeOptions={[10, 50]}
        paginationMode="server"
        rowCount={data?.data?.total ?? 0}
        isPaginationModal={false}
        paginationModel={{
          page: pageIndex - 1,
          pageSize,
        }}
        isNoFilter
        checkboxSelection
        isRowSelectable={(params) =>
          (rowSelectionModel.length < 10 ||
            rowSelectionModel.includes(params?.row?.id as string)) &&
          !isDownloading &&
          params?.row?.status === RequestCandidateEntityStatus.Published
        }
        onRowSelectionModelChange={(newRowSelectionModel, detail) => {
          let rowSelected = newRowSelectionModel as string[];
          if (rowSelected.length > 10) {
            rowSelected = rowSelected.slice(0, 10);
          }
          const idRowSelected = rowSelected.map((e: string) => {
            return detail?.api?.getRowParams(e)?.row; // eslint-disable-line
          });
          setSelectedRequestCandidate(idRowSelected);
          setRowSelectionModel(rowSelected);
        }}
        rowSelectionModel={rowSelectionModel}
      />
      <RequestCandidateCreateComponent
        isModalOpen={isModalCreateOpen}
        setIsModalOpen={setIsModalCreateOpen}
        refetch={refetch}
      />

      <RequestCandidateUpdateComponent
        isModalOpen={isModalEditOpen}
        setIsModalOpen={setIsModalEditOpen}
        refetch={refetch}
        updateId={requestCandidateId ?? ""}
        mode={isEditForm ? ModeForm.Edit : ModeForm.View}
      />
    </>
  );
};
